import React, { useEffect } from 'react';

import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';

import { pageTitles } from '@/constants/pageTitles';
import useReactQuery from '@/hooks/common/useReactQuery';
import useToast from '@/hooks/store/useToast';
import {
  type ParentPasswordChangeFormValues,
  type ParentWithStudents,
  type ProfileEditAuthState,
} from '@/interfaces/parent';

import MyPageTemplate2 from './MyPageTemplate2';
import ParentPasswordChangeForm from './ParentPasswordChangeForm';

const ParentPasswordChangePage: React.FC = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { useMutation, useQuery } = useReactQuery();

  useEffect(() => {
    const state = location.state as ProfileEditAuthState | null;

    if (!state?.authenticated) {
      navigate('/mypage/profile', { replace: true });
      return;
    }
  }, [location.state, navigate]);

  const { refetch: refetchMe } = useQuery<ParentWithStudents>({
    queryKey: ['me'],
    url: '/api/school/parents/me',
    enabled: false,
  });

  const { mutateAsync, isPending, error } = useMutation({
    method: 'PATCH',
    url: '/api/school/parents/me_without_additional_auth',
    onSuccess: async () => {
      // 保護者情報のキャッシュを再フェッチ
      await refetchMe();
      showToast('アカウント情報を保存しました');
      navigate('/mypage/profile');
    },
  });

  const submitErrorMessages =
    error && error.json
      ? [error.json.message || '予期しないエラーが発生しました']
      : [];

  const handleSubmit = (values: ParentPasswordChangeFormValues) => {
    void mutateAsync(JSON.stringify({ parent: values }));
  };

  const handleCancel = () => {
    navigate('/mypage/profile');
  };

  return (
    <MyPageTemplate2>
      <Helmet>
        <title>{pageTitles.mypage}</title>
      </Helmet>
      <ParentPasswordChangeForm
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isPending}
        serverError={submitErrorMessages}
      />
    </MyPageTemplate2>
  );
};

export default ParentPasswordChangePage;
